import { AlertTriangle, ListChecks, PackageCheck, Truck } from 'lucide-react';
import type { TFunction } from 'i18next';

import { KpiCard } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatNumber } from '@/shared/utils/number';

import type { OrderStats } from '../types';

export interface OrderStatsSectionProps {
  stats: OrderStats;
  t: TFunction<'orders'>;
  isLoading?: boolean;
}

function formatSignedPercent(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  const rounded = formatNumber(value, 'decimal');
  if (value > 0) return `+${rounded}%`;
  if (value < 0) return `${rounded}%`;
  return `${rounded}%`;
}

function formatSignedDelta(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (value > 0) return `+${formatNumber(value)}`;
  return formatNumber(value);
}

export function OrderStatsSection({ stats, t, isLoading }: OrderStatsSectionProps) {
  const totalTrend =
    stats.totalDeltaPercent > 0
      ? 'text-success-strong'
      : stats.totalDeltaPercent < 0
        ? 'text-danger-strong'
        : 'text-fg-muted';
  const delayedTrend =
    stats.delayedDelta > 0
      ? 'text-danger-strong'
      : stats.delayedDelta < 0
        ? 'text-success-strong'
        : 'text-fg-muted';

  const comparisonFooter =
    stats.totalComparisonLabel.trim() || t('stats.total.comparisonFallback');

  return (
    <section
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
      aria-label={t('stats.aria')}
      aria-busy={isLoading || undefined}
    >
      <OrderKpiCard
        label={t('stats.total.label')}
        value={formatNumber(stats.totalOrders)}
        footer={comparisonFooter}
        icon={ListChecks}
        iconClassName="text-primary"
        inlineMeta={formatSignedPercent(stats.totalDeltaPercent)}
        inlineMetaClassName={totalTrend}
      />
      <OrderKpiCard
        label={t('stats.pending.label')}
        value={formatNumber(stats.pendingFulfillment)}
        footer={t('stats.pending.footer')}
        icon={PackageCheck}
        iconClassName="text-warning-strong"
        inlineMeta={t('stats.pending.meta')}
        inlineMetaClassName="text-warning-strong"
      />
      <OrderKpiCard
        label={t('stats.outForDelivery.label')}
        value={formatNumber(stats.outForDelivery)}
        footer={t('stats.outForDelivery.footer')}
        icon={Truck}
        iconClassName="text-primary"
      />
      <OrderKpiCard
        label={t('stats.delayed.label')}
        value={formatNumber(stats.delayed)}
        footer={t('stats.delayed.footer')}
        icon={AlertTriangle}
        iconClassName="text-danger-strong"
        valueClassName="text-danger-strong"
        inlineMeta={formatSignedDelta(stats.delayedDelta)}
        inlineMetaClassName={delayedTrend}
      />
    </section>
  );
}

function OrderKpiCard({
  label,
  value,
  footer,
  icon: Icon,
  iconClassName,
  valueClassName,
  inlineMeta,
  inlineMetaClassName,
}: {
  label: string;
  value: string;
  footer: string;
  icon: typeof ListChecks;
  iconClassName?: string;
  valueClassName?: string;
  inlineMeta?: string;
  inlineMetaClassName?: string;
}) {
  return (
    <KpiCard
      className="min-h-[110px] p-5"
      label={
        <span className="flex items-center justify-between gap-3 normal-case tracking-normal">
          <span>{label}</span>
          <Icon
            aria-hidden="true"
            className={cn('h-5 w-5 shrink-0', iconClassName)}
            strokeWidth={2}
          />
        </span>
      }
      value={
        <span className="flex items-baseline gap-2">
          <span className={cn(valueClassName)}>{value}</span>
          {inlineMeta ? (
            <span className={cn('text-sm font-medium', inlineMetaClassName)}>{inlineMeta}</span>
          ) : null}
        </span>
      }
      footer={footer}
    />
  );
}
